import { Typography } from "@/components/atoms";
import { NotEditableReason } from "@/redux/apis/clientBooking/types";
import { clsx } from "@/utils";

interface BookingSheetBannerProps {
  /** `view` = read-only booking; `edit` = editing an existing booking. */
  mode: "view" | "edit";
  /** Display job id, named in both modes ("Editing booking J000123" / "Booking J000123 is closed..."). */
  jobLabel?: string;
  /** Why the booking is locked. Null falls back to wording true of every reason — see VIEW_PREDICATE. */
  notEditableReason?: NotEditableReason | null;
  className?: string;
}

/**
 * Wording true of EVERY locked booking, used when the reason is missing — the trailing clause alone,
 * since the shape's first half names what happened and here we do not know what happened.
 *
 * Missing is a real case, not a defensive one: a frontend deployed ahead of the backend gets no
 * reason at all, and an unrecognised value (a reason added server-side later) lands here too. It has
 * to be honest on its own rather than a placeholder — which is exactly what the old single message
 * was not.
 */
const VIEW_FALLBACK = "This booking is now view-only";

/**
 * One line per reason, keeping the original banner's shape — "{what happened} and is now view-only",
 * so only the first clause varies. The single message this replaced IS the `closed` line, verbatim.
 *
 * Only `closed` and `cancelled` describe a finished job; the rest are live bookings past the point of
 * changing, and the whole point of this map is that they stop being called closed. A driver already on
 * the way is the case that prompted it — a client waiting at the kerb was being told it was closed.
 */
const VIEW_PREDICATE: Record<NotEditableReason, string> = {
  closed: "is closed and is now view-only",
  cancelled: "was cancelled and is now view-only",
  in_progress: "is under way and is now view-only",
  too_close_to_pickup: "is too close to pickup and is now view-only",
  paid: "has been paid and is now view-only",
  special_event: "can't be changed online and is now view-only",
};

/**
 * Subject when there is no job id to name — today's wording, kept verbatim so a missing id is a
 * silent fall back to the copy that shipped rather than a broken "Booking  is closed".
 *
 * `in_progress` is the one reason that has never said "booking": the driver is on the way, and the
 * trip is the thing under way.
 */
const NO_ID_SUBJECT: Partial<Record<NotEditableReason, string>> = { in_progress: "This trip" };

const subjectFor = (reason: NotEditableReason, jobLabel?: string) =>
  jobLabel ? `Booking ${jobLabel}` : NO_ID_SUBJECT[reason] ?? "This booking";

/**
 * The view-only line. An unrecognised reason (added server-side later) has no predicate and lands on
 * the fallback, same as no reason at all — named when we have the id, generic when we do not.
 */
const viewCopy = (notEditableReason?: NotEditableReason | null, jobLabel?: string) => {
  const predicate = notEditableReason ? VIEW_PREDICATE[notEditableReason] : undefined;

  if (!notEditableReason || !predicate) return jobLabel ? `Booking ${jobLabel} is now view-only` : VIEW_FALLBACK;

  return `${subjectFor(notEditableReason, jobLabel)} ${predicate}`;
};

/**
 * Full-width status bar atop the Booking Sheet / Summary, telling the user what state the sheet is in:
 *  - **view** — silver-gray, why the booking can't be changed.
 *  - **edit** — info-blue, "Editing booking {id}" so it's obvious which job is being changed.
 * `sticky top-0` pins it to the top of its scroll container (the aside / slide-in / main column).
 *
 * BOTH modes name the job. The id is what tells a client which of their bookings the bar is about,
 * and it was previously shown only when editing even though view-only had it to hand.
 *
 * Padded on BOTH axes: the reason-specific copy is longer than the single message it replaced, so it
 * now wraps on a narrow viewport — and with no horizontal padding it wrapped flush to the screen edge.
 */
export const BookingSheetBanner = ({ mode, jobLabel, notEditableReason, className }: BookingSheetBannerProps) => (
  <div className={clsx("sticky top-0 z-20 px-4 py-2 text-center", mode === "edit" ? "bg-info" : "bg-neutral-silver-gray", className)}>
    <Typography variant="action" className="text-white">
      {mode === "edit" ? `Editing booking ${jobLabel ?? ""}`.trim() : viewCopy(notEditableReason, jobLabel)}
    </Typography>
  </div>
);
